import { normalizeCategoryText } from './categoryClassifier';
import {
  isReviewRowContinuationText,
  isReviewRowFoodContinuationText,
  normalizeCareContinuationCategory,
} from '../shared/careContinuationRules';

const getRowText = (row = {}) => String(
  row?.body
  || row?.note
  || row?.text
  || row?.content
  || row?.sourceText
  || row?.originalText
  || row?.title
  || ''
).trim();

const getRowDate = (row = {}) => {
  const candidate = row?.date || row?.timestamp || null;
  if (candidate instanceof Date) {
    return Number.isNaN(candidate.getTime()) ? null : candidate;
  }

  if (!candidate) {
    return null;
  }

  const date = new Date(candidate);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getRowDateKey = (row = {}) => {
  if (row?.entryDate) {
    return String(row.entryDate);
  }

  const date = getRowDate(row);
  return date ? date.toDateString() : '';
};

const getRowTimeKey = (row = {}) => String(row?.localTime || '').trim() || String(row?.timeBucket || '').trim();

const getFoodMealType = (row = {}) => {
  const text = normalizeCategoryText(getRowText(row));
  if (!text) {
    return '';
  }

  if (/\bbreakfast\b/i.test(text)) return 'breakfast';
  if (/\blunch\b/i.test(text)) return 'lunch';
  if (/\bdinner\b/i.test(text)) return 'dinner';
  if (/\b(snack|snack time|snack break)\b/i.test(text)) return 'snack';
  if (/\bmeal\b/i.test(text)) return 'meal';

  return '';
};

const hasComparableTiming = (previousRow = {}, nextRow = {}) => {
  const previousDateKey = getRowDateKey(previousRow);
  const nextDateKey = getRowDateKey(nextRow);

  if (previousDateKey && nextDateKey && previousDateKey !== nextDateKey) {
    return false;
  }

  const previousTimeKey = getRowTimeKey(previousRow);
  const nextTimeKey = getRowTimeKey(nextRow);

  if (previousTimeKey || nextTimeKey) {
    return Boolean(previousTimeKey && nextTimeKey && previousTimeKey === nextTimeKey);
  }

  const previousDate = getRowDate(previousRow);
  const nextDate = getRowDate(nextRow);
  if (previousDate && nextDate) {
    return Math.abs(nextDate.getTime() - previousDate.getTime()) <= 5 * 60 * 1000;
  }

  return true;
};

const mergeTextFields = (previousValue = '', nextValue = '') => {
  const previousText = String(previousValue || '').trim();
  const nextText = String(nextValue || '').trim();

  if (!previousText) return nextText;
  if (!nextText) return previousText;
  if (previousText === nextText) return previousText;

  return `${previousText} ${nextText}`.replace(/\s+/g, ' ').trim();
};

const mergeArrayField = (previousValue, nextValue) => {
  const previousItems = Array.isArray(previousValue) ? previousValue : String(previousValue || '').split(',').map((value) => value.trim()).filter(Boolean);
  const nextItems = Array.isArray(nextValue) ? nextValue : String(nextValue || '').split(',').map((value) => value.trim()).filter(Boolean);
  return [...new Set([...previousItems, ...nextItems])];
};

const mergeReviewRows = (previousRow = {}, nextRow = {}) => {
  const mergedText = mergeTextFields(previousRow.note || previousRow.text, nextRow.note || nextRow.text);
  const mergedTitle = mergeTextFields(previousRow.title || previousRow.note || previousRow.text, nextRow.title || nextRow.note || nextRow.text);
  const mergedCategory = normalizeCareContinuationCategory(previousRow.category || nextRow.category);

  return {
    ...previousRow,
    category: mergedCategory,
    body: mergeTextFields(previousRow.body || previousRow.note || previousRow.text, nextRow.body || nextRow.note || nextRow.text),
    note: mergedText,
    text: mergeTextFields(previousRow.text || previousRow.note, nextRow.text || nextRow.note),
    content: mergeTextFields(previousRow.content || previousRow.text || previousRow.note, nextRow.content || nextRow.text || nextRow.note),
    sourceText: mergeTextFields(previousRow.sourceText || previousRow.originalText || previousRow.note, nextRow.sourceText || nextRow.originalText || nextRow.note),
    originalText: mergeTextFields(previousRow.originalText || previousRow.sourceText || previousRow.note, nextRow.originalText || nextRow.sourceText || nextRow.note),
    title: mergedTitle,
    titlePrefix: previousRow.titlePrefix || nextRow.titlePrefix || '',
    reviewConfidence: previousRow.reviewConfidence || nextRow.reviewConfidence || 'high',
    reviewConfidenceLabel: previousRow.reviewConfidenceLabel || nextRow.reviewConfidenceLabel || '',
    reviewConfidenceReasons: [
      ...(Array.isArray(previousRow.reviewConfidenceReasons) ? previousRow.reviewConfidenceReasons : []),
      ...(Array.isArray(nextRow.reviewConfidenceReasons) ? nextRow.reviewConfidenceReasons : []),
    ],
    parsedObservation: Boolean(previousRow.parsedObservation || nextRow.parsedObservation),
    parsedSourceCategory: previousRow.parsedSourceCategory || nextRow.parsedSourceCategory || '',
    parsedSourceConfidence: previousRow.parsedSourceConfidence || nextRow.parsedSourceConfidence || '',
    metadata: previousRow.metadata || nextRow.metadata || null,
    localTime: previousRow.localTime || nextRow.localTime || null,
    timeSource: previousRow.timeSource || nextRow.timeSource || 'missing',
    timeBucket: previousRow.timeBucket || nextRow.timeBucket || '',
    timeInferred: Boolean(previousRow.timeInferred || nextRow.timeInferred),
    timeInferenceReason: previousRow.timeInferenceReason || nextRow.timeInferenceReason || '',
    include: previousRow.include !== false && nextRow.include !== false,
    activityTypes: mergeArrayField(previousRow.activityTypes, nextRow.activityTypes),
  };
};

const shouldMergeFoodRows = (previousRow = {}, nextRow = {}) => {
  const previousMealType = getFoodMealType(previousRow);
  const nextMealType = getFoodMealType(nextRow);
  const nextText = normalizeCategoryText(getRowText(nextRow));
  const previousText = normalizeCategoryText(getRowText(previousRow));

  if (previousMealType && nextMealType && previousMealType !== nextMealType) {
    return false;
  }

  if (nextMealType && !previousMealType) {
    return false;
  }

  const hasFoodContinuationCue = isReviewRowFoodContinuationText(nextText);
  const previousLooksLikeMealAnchor = Boolean(previousMealType) || /\b(?:ate|drank|had|finished|snack|meal|breakfast|lunch|dinner)\b/i.test(previousText);

  return previousLooksLikeMealAnchor && hasFoodContinuationCue;
};

export const groupRowsForReview = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }

  return rows.reduce((groupedRows, row) => {
    if (!row || typeof row !== 'object') {
      return groupedRows;
    }

    const previousRow = groupedRows[groupedRows.length - 1];
    const previousCategory = normalizeCareContinuationCategory(previousRow?.category || '');
    const nextCategory = normalizeCareContinuationCategory(row.category || '');
    const previousText = getRowText(previousRow);
    const nextText = getRowText(row);

    const shouldMerge =
      Boolean(previousRow)
      && previousCategory === nextCategory
      && Boolean(previousText)
      && Boolean(nextText)
      && hasComparableTiming(previousRow, row)
      && (
        (nextCategory === 'food'
          ? shouldMergeFoodRows(previousRow, row)
          : isReviewRowContinuationText(nextCategory, nextText))
      );

    if (!shouldMerge) {
      groupedRows.push({ ...row });
      return groupedRows;
    }

    groupedRows[groupedRows.length - 1] = mergeReviewRows(previousRow, row);
    return groupedRows;
  }, []);
};
